import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { AspectRatioSelector } from '../components/AspectRatioSelector';
import { useSeedancePresetActors } from '../../../../features/seedance-preset-actors';
import type { ExpressSeedanceActor } from '../useExpressMode';
import { EXPRESS_MAX_ACTORS, EXPRESS_DURATION_OPTIONS } from '../useExpressMode';
import { useCredits } from '../../../../hooks/useCredits';
import { useCreditRates } from '../../../../features/organizations/hooks';
import { RESOURCE_TYPE_SCENARIO_GPT6_SOL } from '../../state/useGenerationState';
import styles from '../ExpressModeModal.module.css';

export interface ToneOption {
  value: string;
  label: string;
}

export interface ToneGroup {
  id: string;
  label: string;
  children: ToneOption[];
}

// 絵コンテ静止画と Seedance 2.0 Mini 720p の credit_rate_config resource_type。
// backend の _resolve_image_resource_type / _resolve_video_resource_type と一致。
// 絵コンテは express_job_service が gpt-image-2.5-flare-auto × 1280px (5:8) で生成するため、
// 課金 tier は '-storyboard' (2026-09-09: gpt-image-2-auto の 1K 行を見ていた見積りズレも修正)。
const RESOURCE_TYPE_STORYBOARD_IMAGE = 'gpt-image-2.5-flare-auto-storyboard';
const RESOURCE_TYPE_SEEDANCE_MINI_720 = 'seedance-2-mini-720p';

interface StepSettingsProps {
  tone: string;
  /** ユーザーがトーンを明示操作済みか。false のうちは recommendedTone を「おすすめ」既定
   *  として表示する (計画書§5.4)。 */
  toneTouched: boolean;
  /** 商品AIプロファイルから解決された推奨トーン (未操作時の既定候補)。null は推奨なし。 */
  recommendedTone?: { value: string; label: string } | null;
  aspectRatio: '16:9' | '9:16';
  scenarioCount: number;
  duration: string;
  selectedPresetActors: ExpressSeedanceActor[];
  toneGroups: ToneGroup[];
  onToneChange: (tone: string) => void;
  onAspectRatioChange: (ratio: '16:9' | '9:16') => void;
  onScenarioCountChange: (count: number) => void;
  onDurationChange: (duration: string) => void;
  onPresetActorsChange: (actors: ExpressSeedanceActor[]) => void;
  onGenerate: () => void;
  onFullGenerate: () => void;
  productTitle?: string;
  productImageUrl?: string;
}

export function StepSettings({
  tone,
  toneTouched,
  recommendedTone,
  aspectRatio,
  scenarioCount,
  duration,
  selectedPresetActors,
  toneGroups,
  onToneChange,
  onAspectRatioChange,
  onScenarioCountChange,
  onDurationChange,
  onPresetActorsChange,
  onGenerate,
  onFullGenerate,
  productTitle,
  productImageUrl,
}: StepSettingsProps) {
  // All groups expanded by default
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // トーンセクション全体の開閉。トーン未選択時は開く / 選択済みなら畳んだ状態で開始。
  const [toneSectionOpen, setToneSectionOpen] = useState<boolean>(() => !tone);
  const [actorSectionOpen, setActorSectionOpen] = useState(false);
  const [genderFilter, setGenderFilter] = useState<string>('none');
  const [ageFilter, setAgeFilter] = useState<number | null>(null);

  const toggleGroup = useCallback((groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  // 想定尺スライダーの範囲は EXPRESS_DURATION_OPTIONS (4〜15秒) の両端から導出する。
  const durationMin = Number(EXPRESS_DURATION_OPTIONS[0].value);
  const durationMax = Number(EXPRESS_DURATION_OPTIONS[EXPRESS_DURATION_OPTIONS.length - 1].value);

  const handleToneSelect = (value: string) => {
    const isDeselect = tone === value;
    onToneChange(isDeselect ? '' : value);
    // トーンを選択したらセクション全体を畳む (選択解除時は開いたまま)。
    if (!isDeselect) {
      setToneSectionOpen(false);
    }
  };

  // 折りたたんだ後に中途半端な位置に残らないよう、トーンセクション先頭へスクロールする。
  const toneSectionRef = useRef<HTMLDivElement>(null);
  const toneFirstRender = useRef(true);
  useEffect(() => {
    if (toneFirstRender.current) {
      toneFirstRender.current = false;
      return;
    }
    if (!toneSectionOpen) {
      toneSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [toneSectionOpen]);

  // 推奨トーン (計画書§5.4)。実際に選択肢として表示されている (toneGroups に存在する)
  // ときだけ「おすすめ」既定として扱う。tree/legacy 経路の値ドメイン差異などで
  // 選択肢に無い値が来た場合は無視する (安全側フォールバック)。
  const recommendedOption = useMemo(() => {
    if (!recommendedTone) return null;
    for (const g of toneGroups) {
      const opt = g.children.find(c => c.value === recommendedTone.value);
      if (opt) return opt;
    }
    return null;
  }, [recommendedTone, toneGroups]);
  // ユーザー未操作かつ有効な推奨がある間は、推奨トーンを既定選択として表示する。
  const isRecommendationActive = !toneTouched && !!recommendedOption;
  const effectiveTone = isRecommendationActive ? recommendedOption!.value : tone;

  // ヘッダーに表示する選択中トーン名。推奨既定中は「おすすめ: {label}」、未選択は「指定なし」。
  const toneSummary = useMemo(() => {
    if (isRecommendationActive) return `おすすめ: ${recommendedOption!.label}`;
    if (!tone) return '指定なし';
    for (const g of toneGroups) {
      const opt = g.children.find(c => c.value === tone);
      if (opt) return opt.label;
    }
    return tone;
  }, [tone, toneGroups, isRecommendationActive, recommendedOption]);

  const isActorSelected = useCallback(
    (id: string) => selectedPresetActors.some(a => a.id === id),
    [selectedPresetActors],
  );
  const atMaxActors = selectedPresetActors.length >= EXPRESS_MAX_ACTORS;

  const handleToggleActor = (actor: ExpressSeedanceActor) => {
    if (isActorSelected(actor.id)) {
      onPresetActorsChange(selectedPresetActors.filter(a => a.id !== actor.id));
    } else {
      // 上限に達していれば追加しない (カードは disabled で 3 人目はクリック不可)。
      if (atMaxActors) return;
      onPresetActorsChange([...selectedPresetActors, actor]);
    }
  };

  const filters = useMemo(() => {
    const f: { gender?: 'male' | 'female' | 'neutral'; age?: number } = {};
    if (genderFilter === 'male' || genderFilter === 'female') f.gender = genderFilter;
    if (ageFilter !== null) f.age = ageFilter;
    return f;
  }, [genderFilter, ageFilter]);

  const { data: creditsData } = useCredits();
  const { data: creditRatesData } = useCreditRates();

  // 選んだ想定尺がそのまま Seedance 出力尺になる (4..15 にクランプ)。見積りもこの秒数で算出。
  const videoDurationSec = useMemo(() => {
    const sec = Number(duration) || 15;
    return Math.max(4, Math.min(15, sec));
  }, [duration]);

  const creditEstimate = useMemo(() => {
    if (!creditRatesData?.rates) return null;
    const rates = creditRatesData.rates;
    const find = (service: string, resource: string) =>
      rates.find((r) => r.service_type === service && r.resource_type === resource);

    const scenarioRate = find('scenario_generation', RESOURCE_TYPE_SCENARIO_GPT6_SOL);
    const storyboardRate = find('image_generation', RESOURCE_TYPE_STORYBOARD_IMAGE);
    const seedanceRate = find('video_generation', RESOURCE_TYPE_SEEDANCE_MINI_720);

    const scenarioCost = (scenarioRate?.credits_per_unit ?? 0) * scenarioCount;
    // 絵コンテ静止画 1 枚 / 案。「シナリオ生成」も全案の絵コンテまで生成するため見積りに含める。
    const storyboardCost = (storyboardRate?.credits_per_unit ?? 0) * scenarioCount;
    // Seedance 動画 (秒数 × 単価) / 案。「一気に動画生成」のみ。
    const videoCost = (seedanceRate?.credits_per_unit ?? 0) * videoDurationSec * scenarioCount;

    return {
      // シナリオ生成ボタン: シナリオ + 絵コンテ画像
      scenarioOnly: scenarioCost + storyboardCost,
      // 一気に動画生成ボタン: シナリオ + 絵コンテ + 動画
      full: scenarioCost + storyboardCost + videoCost,
    };
  }, [creditRatesData, scenarioCount, videoDurationSec]);

  const totalAvailable = creditsData?.total_available ?? creditsData?.credits_balance ?? 0;

  const actorsQuery = useSeedancePresetActors(filters);

  const actorSummary = selectedPresetActors.length === 0
    ? '指定なし'
    : selectedPresetActors.map(a => a.display_name).join('・');

  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepTitle}>シナリオ設定</h2>
      {productTitle && (
        <div className={styles.stepProductRow}>
          {productImageUrl && (
            <img src={productImageUrl} alt="" className={styles.stepProductThumb} />
          )}
          <p className={styles.stepDescription}>{productTitle}</p>
        </div>
      )}

      <div className={styles.settingsGrid}>
        <div className={styles.formGroup} ref={toneSectionRef}>
          <label className={styles.formLabel}>トーン</label>
          <div
            className={`${styles.actorSectionHeader} ${effectiveTone ? styles.actorSectionHeaderActive : ''}`}
            onClick={() => setToneSectionOpen(prev => !prev)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setToneSectionOpen(prev => !prev);
              }
            }}
          >
            <span className={styles.actorSectionLabel}>{toneSummary}</span>
            <span className={styles.toneHeaderActions}>
              {/* トーン選択済み、または推奨既定が適用中のときに表示するクリア (✕) ボタン。
                  推奨既定中にクリアすると toneTouched=true になり「指定なし」に確定する
                  (計画書§5.4: 推奨には戻らない)。シェブロンの左に置く。 */}
              {(tone || isRecommendationActive) && (
                <button
                  type="button"
                  className={styles.toneClearButton}
                  onClick={(e) => { e.stopPropagation(); onToneChange(''); }}
                  aria-label="トーンをクリア"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              <svg
                width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className={`${styles.toneGroupArrow} ${toneSectionOpen ? styles.toneGroupArrowOpen : ''}`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </div>
          {toneSectionOpen && (
          <div className={styles.toneGroups}>
            {toneGroups.map(group => {
              const isCollapsed = collapsedGroups.has(group.id);
              const hasSelected = group.children.some(c => c.value === effectiveTone);

              return (
                <div key={group.id} className={styles.toneGroupSection}>
                  <button
                    type="button"
                    className={`${styles.toneGroupHeader} ${hasSelected ? styles.toneGroupHeaderActive : ''}`}
                    onClick={() => toggleGroup(group.id)}
                  >
                    <span className={styles.toneGroupLabel}>{group.label}</span>
                    <svg
                      width="14" height="14" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round"
                      className={`${styles.toneGroupArrow} ${isCollapsed ? '' : styles.toneGroupArrowOpen}`}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {!isCollapsed && (
                    <div className={styles.toneChildren}>
                      {group.children.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`${styles.toneChildChip} ${effectiveTone === opt.value ? styles.toneChildChipActive : ''}`}
                          onClick={() => handleToneSelect(opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>アスペクト比</label>
          <AspectRatioSelector value={aspectRatio} onChange={onAspectRatioChange} />
        </div>

        <div className={styles.settingsRow2}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>生成案数</label>
            <select
              className={styles.formSelect}
              value={scenarioCount}
              onChange={(e) => onScenarioCountChange(Number(e.target.value))}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n}案</option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              想定尺<span className={styles.durationValue}>{Number(duration) || durationMax}秒</span>
            </label>
            <input
              type="range"
              min={durationMin}
              max={durationMax}
              step={1}
              value={Number(duration) || durationMax}
              onChange={(e) => onDurationChange(e.target.value)}
              className={styles.durationSlider}
            />
            {/* うっすらとした目盛り (各秒数の位置。つまみの可動範囲に合わせて左右に half-thumb 余白) */}
            <div className={styles.durationTicks} aria-hidden="true">
              {EXPRESS_DURATION_OPTIONS.map(opt => (
                <span key={opt.value} className={styles.durationTick} />
              ))}
            </div>
          </div>
        </div>

        {/* 登場人物 (Seedance preset actor)。選んだ人数が登場人物数になる (最大2人)。 */}
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>登場人物（最大2人）</label>
          <button
            type="button"
            className={`${styles.actorSectionHeader} ${selectedPresetActors.length > 0 ? styles.actorSectionHeaderActive : ''}`}
            onClick={() => setActorSectionOpen(prev => !prev)}
          >
            <span className={styles.actorSectionLabel}>{actorSummary}</span>
            <svg
              width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
              className={`${styles.toneGroupArrow} ${actorSectionOpen ? styles.toneGroupArrowOpen : ''}`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {actorSectionOpen && (
            <div className={styles.actorSectionBody}>
              {/* フィルタ */}
              <div className={styles.actorFilters}>
                {/* 性別 */}
                <div className={styles.actorFilterGroup}>
                  <span className={styles.actorFilterLabel}>性別</span>
                  <div className={styles.actorFilterButtons}>
                    <button
                      type="button"
                      className={`${styles.actorFilterChip} ${genderFilter === 'none' ? styles.actorFilterChipActive : ''}`}
                      onClick={() => setGenderFilter('none')}
                    >
                      指定なし
                    </button>
                    <button
                      type="button"
                      className={`${styles.actorFilterChip} ${genderFilter === 'male' ? styles.actorFilterChipActive : ''}`}
                      onClick={() => setGenderFilter('male')}
                    >
                      男性
                    </button>
                    <button
                      type="button"
                      className={`${styles.actorFilterChip} ${genderFilter === 'female' ? styles.actorFilterChipActive : ''}`}
                      onClick={() => setGenderFilter('female')}
                    >
                      女性
                    </button>
                  </div>
                </div>
                {/* 年代 */}
                <div className={styles.actorFilterGroup}>
                  <span className={styles.actorFilterLabel}>年代</span>
                  <div className={styles.actorFilterButtons}>
                    <button
                      type="button"
                      className={`${styles.actorFilterChip} ${ageFilter === null ? styles.actorFilterChipActive : ''}`}
                      onClick={() => setAgeFilter(null)}
                    >
                      指定なし
                    </button>
                    <div className={styles.actorAgeRow}>
                      <input
                        type="number"
                        min={10}
                        max={80}
                        value={ageFilter ?? ''}
                        placeholder="30"
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          if (!Number.isNaN(next)) setAgeFilter(next);
                        }}
                        onFocus={() => {
                          if (ageFilter === null) setAgeFilter(30);
                        }}
                        className={`${styles.actorAgeInput} ${ageFilter !== null ? styles.actorAgeInputActive : ''}`}
                      />
                      <span className={styles.actorAgeUnit}>歳</span>
                      <input
                        type="range"
                        min={10}
                        max={80}
                        value={ageFilter ?? 30}
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          if (!Number.isNaN(next)) setAgeFilter(next);
                        }}
                        className={`${styles.actorAgeSlider} ${ageFilter !== null ? styles.actorAgeSliderActive : ''}`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* preset actor カルーセル (複数選択・最大2人) */}
              <div className={styles.actorCarousel}>
                <div className={styles.actorScrollContainer}>
                  <ul className={styles.actorGrid}>
                    <li>
                      <button
                        type="button"
                        className={`${styles.actorCard} ${styles.actorCardNone} ${selectedPresetActors.length === 0 ? styles.actorCardSelected : ''}`}
                        onClick={() => onPresetActorsChange([])}
                      >
                        <div className={styles.actorImageNone}>
                          <div className={styles.actorNoneIcon} />
                        </div>
                        <div className={styles.actorMeta}>指定なし</div>
                      </button>
                    </li>
                    {actorsQuery.isLoading ? (
                      <li className={styles.actorLoading}>読み込み中…</li>
                    ) : actorsQuery.isError ? (
                      <li className={styles.actorLoading}>取得に失敗しました</li>
                    ) : actorsQuery.data?.actors?.map((actor) => {
                      const selected = isActorSelected(actor.id);
                      const order = selectedPresetActors.findIndex(a => a.id === actor.id);
                      const personLetter = selected && order >= 0 ? String.fromCharCode(65 + order) : null;
                      const disabled = !selected && atMaxActors;
                      return (
                        <li key={actor.id}>
                          <button
                            type="button"
                            className={`${styles.actorCard} ${selected ? styles.actorCardSelected : ''}`}
                            onClick={() => handleToggleActor({
                              id: actor.id,
                              byteplus_asset_id: actor.byteplus_asset_id,
                              display_name: actor.display_name,
                              thumbnail_public_url: actor.thumbnail_public_url ?? undefined,
                              gender: actor.gender ?? undefined,
                              min_age: actor.min_age ?? undefined,
                              max_age: actor.max_age ?? undefined,
                              ethnicity: actor.ethnicity ?? undefined,
                            })}
                            disabled={disabled}
                            aria-pressed={selected}
                            style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                          >
                            {personLetter && (
                              <span className={styles.actorExclusiveBadge}>{`登場人物${personLetter}`}</span>
                            )}
                            <div className={styles.actorImage}>
                              {actor.thumbnail_public_url ? (
                                <img src={actor.thumbnail_public_url} alt={actor.display_name} />
                              ) : (
                                <div className={styles.actorImagePlaceholder} />
                              )}
                            </div>
                            <div className={styles.actorMeta}>{actor.display_name}</div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.buttonRow}>
        <button type="button" className={styles.primaryButton} onClick={onGenerate}>
          <span className={styles.buttonMainRow}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            絵コンテを生成
          </span>
          {creditEstimate && (
            <span className={styles.buttonCreditBadge}>
              {Math.floor(creditEstimate.scenarioOnly).toLocaleString()}ポイント
            </span>
          )}
        </button>
        <button type="button" className={styles.fullGenerateButton} onClick={onFullGenerate}>
          <span className={styles.buttonMainRow}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              <polygon points="6 2 0 10 5 10 4 16 10 8 5 8 6 2" opacity="0.5" />
            </svg>
            一気に動画生成まで
          </span>
          {creditEstimate && (
            <span className={styles.buttonCreditBadge}>
              {Math.floor(creditEstimate.full).toLocaleString()}ポイント（予想）
            </span>
          )}
        </button>
      </div>
      {creditsData && (
        <div className={styles.creditBalanceRow}>
          <span className={styles.creditBalanceLabel}>ポイント残高</span>
          <span className={styles.creditBalanceValue}>{Math.floor(totalAvailable).toLocaleString()}ポイント</span>
        </div>
      )}
    </div>
  );
}
